import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { detectSchemaFromRows } from "./utils/parser";
import { ChatProvider, ChatContext } from "./context/ChatContext";
import Layout from "./components/layout/Layout";
import Settings from "./components/screens/Settings";
import Profile from "./components/screens/Profile";
import getTheme from "./theme";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import Login from "./components/auth/Login";
import Signup from "./components/auth/Signup";
import { createNewChatIfAllowed } from "./utils/sessionManager";
import { fetchUserHistory, getSessionData } from "./services/api";
import {
  saveUserHistory,
  getUserHistory,
  clearUserHistory,
} from "./utils/storage"; // Assuming these utilities exist and use userId

function PrivateRoute({ children }) {
  const { user } = useContext(AuthContext);
  return user ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user } = useContext(AuthContext);
  return !user ? children : <Navigate to="/layout" replace />;
}

// Normalize session object
const normalizeSession = (conv) => ({
  id: conv.Session_Id || conv.id || Date.now(),
  type: "conversation",
  name: conv.Title || conv.name || "New Chat",
  messages: conv.messages || [],
  rows: conv.rows || [],
  schema: conv.schema || null,
  timestamp: conv.timestamp || Date.now(),
});

function AppRoutes({ darkMode, setDarkMode }) {
  const { user } = useContext(AuthContext);
  // Get setters from ChatContext to control the active chat state
  const { setMessages, setSchema: setChatSchema } = useContext(ChatContext);

  const [rows, setRows] = useState([]);
  const [schema, setSchema] = useState(null);

  const [history, setHistory] = useState(
    // ➡️ CRITICAL FIX: Initialize history ONLY if a userId is available.
    () => (user?.userId ? getUserHistory(user.userId) || [] : [])
  );

  const [activeConvId, setActiveConvId] = useState(
    history.length > 0 ? history[0].id : null
  );
  const chatRef = useRef(null);

  // Sync localStorage whenever history changes
  useEffect(() => {
    // Ensure history is saved per user
    if (user?.userId) saveUserHistory(user.userId, history);
  }, [history, user?.userId]);

  // Load history from API
  const loadHistory = async () => {
    if (!user || !user.userId) {
      // Explicit check
      setHistory([]); // Clear local state if no user
      return;
    }

    try {
      const apiHistory = await fetchUserHistory(user.userId);
      const normalized = apiHistory.Sucess.map(normalizeSession);

      // Merge API history with localStorage, avoid duplicates
      // getUserHistory must use the userId to retrieve the correct set of history
      const localHistory = getUserHistory(user.userId) || [];
      const merged = [...normalized, ...localHistory].reduce((acc, item) => {
        if (!acc.find((c) => c.id === item.id)) acc.push(item);
        return acc;
      }, []);

      setHistory(merged);
      saveUserHistory(user.userId, merged);

      if (merged.length > 0) setActiveConvId(merged[0].id);
    } catch (err) {
      // Fallback: If API fails, ensure we load local storage history for the user
      setHistory(getUserHistory(user.userId) || []);
    }
  };

  // ➡️ EFFECT: Runs on user login/logout to load the correct history
  useEffect(() => {
    // Reset active chat window state
    setMessages([]);
    setChatSchema(null);
    setRows([]);
    setSchema(null);
    setActiveConvId(null);

    // Load the correct history for the new user or clear if logged out
    loadHistory();
  }, [user]); // Depend on 'user' object

  // Add message to active session
  function addMessageToHistory(message) {
    setHistory((prev) => {
      const newPrev = [...prev];
      const idx = newPrev.findIndex((c) => c.id === activeConvId);
      if (idx >= 0) {
        const conv = { ...newPrev[idx] };
        conv.messages = [...(conv.messages || []), message];
        if (conv.messages.length === 1 && message.role === "user") {
          conv.name = message.text.slice(0, 30);
        }
        newPrev[idx] = conv;
      }
      return newPrev;
    });

    if (chatRef.current?.onIncoming) chatRef.current.onIncoming(message);
  }

  // Handle file upload → filename becomes title
  // async function handleFileParsed(parsedRows, filename, file) {
  //   if (!user?.userId) return;
  //   try {
  //     const s = await detectSchemaFromRows(file, parsedRows, user.userId);
  //     setRows(parsedRows);
  //     setSchema(s.schema || null);
  //     setChatSchema(s.schema || null); // Update ChatContext schema

  //     // 🔹 Save uploaded file’s meta for API usage
  //     if (s.meta) {
  //       localStorage.setItem("file_info", JSON.stringify(s.meta));
  //     }

  //     let sessionId = activeConvId || Date.now();

  //     setHistory((prev) => {
  //       const newPrev = [...prev];
  //       const idx = newPrev.findIndex((c) => c.id === sessionId);

  //       const fileMessage = {
  //         role: "assistant",
  //         text: `✅ File uploaded: ${filename}`,
  //         meta: { schema: s.schema, ...s.meta, schemaUpdate: true },
  //       };

  //       if (idx >= 0) {
  //         const conv = { ...newPrev[idx] };
  //         conv.name = filename;
  //         conv.messages = [...(conv.messages || []), fileMessage];
  //         conv.rows = parsedRows;
  //         conv.schema = s;
  //         conv.timestamp = Date.now();
  //         newPrev[idx] = conv;
  //       } else {
  //         const newConv = normalizeSession({
  //           Session_Id: sessionId,
  //           Title: filename,
  //           messages: [fileMessage],
  //           rows: parsedRows,
  //           schema: s,
  //         });
  //         newConv.messages = [fileMessage]; // ensure file message is present
  //         newPrev.unshift(newConv);
  //       }

  //       saveUserHistory(user.userId, newPrev);
  //       return newPrev;
  //     });

  //     setActiveConvId(sessionId);

  //     // Update active messages in ChatContext for immediate display
  //     setMessages([
  //       {
  //         role: "assistant",
  //         text: `✅ File uploaded: ${filename}`,
  //       },
  //     ]);

  //     if (chatRef.current?.resetChat) chatRef.current.resetChat(); // Clear old messages
  //     if (chatRef.current?.onIncoming)
  //       chatRef.current.onIncoming({
  //         role: "assistant",
  //         text: `✅ File uploaded: ${filename}`,
  //       });
  //   } catch (err) {

  //     setRows(parsedRows);
  //     setSchema(null);
  //     setChatSchema(null); // Update ChatContext schema
  //   }
  // }
  async function handleFileParsed(parsedRows, filename, file) {
    if (!user?.userId) return;

    try {
      // ✅ Show uploading message in Chat UI immediately
      if (chatRef.current?.onIncoming) {
        chatRef.current.onIncoming({
          role: "assistant",
          text: `📤 Uploading ${filename}... Please wait while I detect the schema.`,
        });
      }

      // Run schema detection
      const result = await detectSchemaFromRows(file, parsedRows, user.userId);

      if (result.status === "error" || result.status === "no_schema") {
        // ❌ Show a friendly error message
        if (chatRef.current?.onIncoming) {
          chatRef.current.onIncoming({
            role: "assistant",
            text: "⚠️ Sorry, I couldn’t detect the schema for this file. Try re-uploading or use a different file format (CSV/XLSX).",
          });
        }
        // setSchema(null);
        // setChatSchema(null);
        // setRows(parsedRows);
        return;
      }

      // ✅ Schema detected successfully
      const { schema: detectedSchema, meta } = result;

      setRows(parsedRows);
      setSchema(detectedSchema);
      setChatSchema(detectedSchema);

      if (meta) localStorage.setItem("file_info", JSON.stringify(meta));

      const sessionId = activeConvId || Date.now();

      setHistory((prev) => {
        const newPrev = [...prev];
        const idx = newPrev.findIndex((c) => c.id === sessionId);

        const fileMessage = {
          role: "assistant",
          text: `✅ File uploaded: ${filename}`,
          meta: { schema: detectedSchema, ...meta, schemaUpdate: true },
        };

        if (idx >= 0) {
          const conv = { ...newPrev[idx] };
          conv.name = filename;
          conv.messages = [...(conv.messages || []), fileMessage];
          conv.rows = parsedRows;
          conv.schema = detectedSchema;
          conv.timestamp = Date.now();
          newPrev[idx] = conv;
        } else {
          const newConv = normalizeSession({
            Session_Id: sessionId,
            Title: filename,
            messages: [fileMessage],
            rows: parsedRows,
            schema: detectedSchema,
          });
          newPrev.unshift(newConv);
        }

        saveUserHistory(user.userId, newPrev);
        return newPrev;
      });

      setActiveConvId(sessionId);

      // ✅ Let ChatArea show confirmation message
      if (chatRef.current?.onIncoming) {
        chatRef.current.onIncoming({
          role: "assistant",
          text: `✅ File uploaded and schema detected for ${filename}. You can now query your data.`,
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      if (chatRef.current?.onIncoming) {
        chatRef.current.onIncoming({
          role: "assistant",
          text: "⚠️ Upload failed. Please check your network and try again.",
        });
      }
    }
  }

  // Open a past session
  const handleOpenHistory = async (conv) => {
    if (!conv) return;

    let normalizedConv = normalizeSession(conv);

    try {
      if (!normalizedConv.messages || normalizedConv.messages.length === 0) {
        // Fetch messages if not present (from API)
        const messages = await getSessionData(normalizedConv.id, user.userId);
        normalizedConv.messages = Array.isArray(messages) ? messages : [];

        // Update the history state with fetched messages and persist
        setHistory((prev) => {
          const newHistory = [...prev];
          const idx = newHistory.findIndex((c) => c.id === normalizedConv.id);
          if (idx !== -1) {
            newHistory[idx] = normalizedConv;
            saveUserHistory(user.userId, newHistory);
            return newHistory;
          }
          return prev;
        });
      }
    } catch (err) {
      normalizedConv.messages = [];
    }

    setRows(normalizedConv.rows || []);
    setSchema(normalizedConv.schema || null);

    // Crucial: Update the active chat's schema and messages using ChatContext setters
    setChatSchema(normalizedConv.schema || null);
    setMessages(normalizedConv.messages);

    // 🔹 Save correct meta for this history into localStorage
    if (normalizedConv.schema?.meta) {
      localStorage.setItem(
        "file_info",
        JSON.stringify(normalizedConv.schema.meta)
      );
    }

    setActiveConvId(normalizedConv.id);
  };

  // Clear history
  function handleClearHistory() {
    if (user?.userId) clearUserHistory(user.userId); // Clear specific user's storage
    setHistory([]); // Clear current state
    handleNewChat();
  }

  const handleNewChat = async () => {
    if (!user?.userId) return;

    const newChat = await createNewChatIfAllowed(user.userId);

    if (newChat) {
      setActiveConvId(newChat.id);

      // Reset chat state
      setMessages([]);
      setChatSchema(null);
      setRows([]);
      setSchema(null);
    } else {
    }
  };

  return (
    <Routes>
      <Route
        path="/layout"
        element={
          <PrivateRoute>
            <Layout
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              history={history} // Pass user-specific history
              onFileParsed={handleFileParsed}
              onOpenHistory={handleOpenHistory}
              onClearHistory={handleClearHistory}
              rows={rows}
              schema={schema}
              chatRef={chatRef}
              onNewChat={handleNewChat}
              activeConvId={activeConvId}
              onSchemaUpdate={(newSchema) => {
                addMessageToHistory({
                  role: "assistant",
                  text: "✅ Schema confirmed & updated.",
                  meta: { schema: newSchema, schemaUpdate: true },
                });
                setSchema(newSchema);
                setChatSchema(newSchema); // Update ChatContext schema
              }}
              onMessage={addMessageToHistory}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  const [darkMode, setDarkMode] = useState(true);
  const theme = useMemo(() => getTheme(darkMode), [darkMode]);

  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ChatProvider>
          <BrowserRouter>
            <AppRoutes darkMode={darkMode} setDarkMode={setDarkMode} />
          </BrowserRouter>
        </ChatProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
